import { useTranslations } from '@/hooks/useTranslations';
import { useIsMobile } from '@/hooks/use-mobile';

/**
 * Hook that automatically returns mobile-optimized translations on mobile devices.
 * Uses the _mobile suffix for translation keys when on mobile and the translation exists.
 * 
 * Usage:
 * const { tm } = useMobileTranslation();
 * <p>{tm('hero.description', 'Fallback text')}</p>
 * 
 * For this to work, add translations with _mobile suffix in the database:
 * - Key: hero.description (desktop version)
 * - Key: hero.description_mobile (mobile version - shorter text)
 */
export const useMobileTranslation = () => {
  const { t, tMobile, language, isLoading } = useTranslations();
  const isMobile = useIsMobile();

  // tm = "translate mobile-aware"
  // On mobile devices, tries to find _mobile version first
  const tm = (key: string, fallback?: string): string => {
    if (isMobile) {
      return tMobile(key, fallback);
    }
    return t(key, fallback);
  };

  return { tm, t, language, isLoading, isMobile };
};
